import { useState } from "react";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "./AppSidebar";
import { SyncErrorBanner } from "./SyncErrorBanner";
import { RequireSession } from "./RequireSession";
import { useOrganization } from "@/hooks/useOrganization";
import { useLanguage } from "@/hooks/useLanguage";
import { Skeleton } from "@/components/ui/skeleton";
import { BillingBanner } from "@/components/billing/BillingBanner";
import { ReadOnlyBanner } from "@/components/billing/ReadOnlyBanner";
import { usePlanGate } from "@/hooks/usePlanGate";
import { TrialAutoStart } from "@/components/billing/TrialAutoStart";
import { TermsGate } from "@/components/auth/TermsGate";
import { Logo } from "@/components/Logo";
import { TopoTexture } from "@/components/TopoTexture";
import { useMode } from "@/hooks/useMode";
import { AppHeadTags } from "@/components/pwa/AppHeadTags";
import { InstallHint } from "@/components/pwa/InstallHint";
import { CaptureFab } from "@/components/capture/CaptureFab";
import { cn } from "@/lib/utils";
import { DemoBanner, DemoBannerSpacer } from "@/components/demo/DemoBanner";

interface AppLayoutProps {
  children: React.ReactNode;
}

// SidebarProvider writes its open/collapsed state to this cookie but never reads
// it back. Every page mounts its own AppLayout, so without this the collapsed
// rail would spring open again on each navigation.
function readSidebarOpen(): boolean {
  if (typeof document === "undefined") return true;
  const match = document.cookie.match(/(?:^|;\s*)sidebar:state=(true|false)/);
  return match ? match[1] === "true" : true;
}

/** The outline of an app page (title, figures, a table) while the company resolves. */
function PageSkeleton() {
  const { language } = useLanguage();
  return (
    <div
      className="space-y-6"
      role="status"
      aria-busy="true"
      aria-label={language === "cs" ? "Načítání" : "Loading"}
    >
      <div className="space-y-2">
        <Skeleton className="h-7 w-56" />
        <Skeleton className="h-4 w-80 max-w-full" />
      </div>
      <div className="flex flex-wrap gap-6">
        <Skeleton className="h-12 w-40" />
        <Skeleton className="h-12 w-40" />
        <Skeleton className="h-12 w-40" />
      </div>
      <Skeleton className="h-72 w-full rounded-xl" />
    </div>
  );
}

export function AppLayout({ children }: AppLayoutProps) {
  const { mode } = useMode();
  const { isLoading: orgLoading, currentOrganization } = useOrganization();
  // Business pages are about one company: until it is known, show the page's
  // outline, not the page itself (its empty states would read as "no data").
  const resolvingCompany = mode === "business" && (orgLoading || !currentOrganization);
  const [defaultOpen] = useState(readSidebarOpen);
  const { active: planGatesActive, entitlements } = usePlanGate();

  // Layout component for app pages. The sidebar carries the org/mode switcher
  // and its own collapse toggle, so desktop has no top bar.
  // Every app page renders inside this layout, so this is its one sign-in guard.
  return (
    <RequireSession>
      <TermsGate>
        {/* Independent of the banners below: a new account is read-only until its trial
          starts. Inside TermsGate, so a Google / Microsoft account accepts the terms first. */}
        <TrialAutoStart />
        {/* Manifest and iOS home-screen tags: the app is installable, the website isn't. */}
        <AppHeadTags />
        <SidebarProvider defaultOpen={defaultOpen}>
          <div className="flex min-h-screen w-full bg-background" data-mode={mode}>
            <AppSidebar />
            <main className="flex min-w-0 flex-1 flex-col">
              {/* Phones: the sidebar is a sheet, so it needs a trigger here. */}
              <header className="flex h-14 items-center gap-3 border-b border-border bg-card px-4 md:hidden">
                <SidebarTrigger />
                <Logo size="sm" />
              </header>
              {/* isolate + -z-10: the contour band sits behind every page's content. */}
              {/* Phones, business mode: pb-24 keeps the last row clear of the capture button. */}
              <div
                data-scroll-root
                className={cn(
                  "relative isolate min-w-0 flex-1 overflow-auto p-6",
                  mode === "business" && "pb-24 md:pb-6"
                )}
              >
                {/* The mode's accent: green for business (the deeper action green in
                light mode, the brand green fades out on white), blue for personal. */}
                <TopoTexture
                  className={
                    mode === "private"
                      ? "inset-x-0 top-0 -z-10 h-80 text-[hsl(var(--personal-primary))] opacity-[0.22] dark:opacity-[0.14]"
                      : "inset-x-0 top-0 -z-10 h-80 text-[hsl(var(--action))] opacity-[0.24] dark:text-primary dark:opacity-[0.13]"
                  }
                />
                {/* Content is capped and centered: past ~1700px headers, summaries and
                table rows get too far apart to read. The contour band stays full
                width, so wide screens keep their texture in the margins. */}
                <div className="mx-auto w-full max-w-[1680px]">
                  {/* Sample-data mode (src/lib/demo): always on top, never dismissable. */}
                  <DemoBanner />
                  {/* Read-only (no plan, enforced) says what still works; otherwise trial days
                    left / failed payment. Both render nothing unless billing is visible. */}
                  {planGatesActive && entitlements?.readOnly ? (
                    <ReadOnlyBanner />
                  ) : (
                    <BillingBanner />
                  )}
                  {/* Phones: once, until installed or closed. */}
                  <InstallHint />
                  {resolvingCompany ? <PageSkeleton /> : children}
                  <DemoBannerSpacer />
                </div>
              </div>
            </main>
          </div>
          <SyncErrorBanner />
          <CaptureFab />
        </SidebarProvider>
      </TermsGate>
    </RequireSession>
  );
}
